import {
  WEB_PLAN_PRICES,
  type WebBillingCurrency,
} from "./localizedPricing";

const PENDING_PURCHASE_KEY = "stalkie_pending_purchase_context";

type WebPlan = "weekly" | "monthly";

export type PendingPurchaseContext = {
  handle?: string;
  previewSessionId?: string;
  source: "public_paywall" | "onboarding_paywall" | "app_renewal";
  checkoutSessionId?: string;
  plan?: "weekly" | "monthly";
  currency?: "brl" | "usd";
  value?: number;
  createdAt: number;
};

export function savePendingPurchaseContext(context: Omit<PendingPurchaseContext, "createdAt">) {
  try {
    sessionStorage.setItem(
      PENDING_PURCHASE_KEY,
      JSON.stringify({
        ...context,
        createdAt: Date.now(),
      }),
    );
  } catch {
    // Session storage can be unavailable in private contexts.
  }
}

export function getPendingPurchaseContext(): PendingPurchaseContext | null {
  try {
    const raw = sessionStorage.getItem(PENDING_PURCHASE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<PendingPurchaseContext>;
    if (typeof parsed.createdAt !== "number") return null;

    const maxAgeMs = 60 * 60 * 1000;
    if (Date.now() - parsed.createdAt > maxAgeMs) {
      sessionStorage.removeItem(PENDING_PURCHASE_KEY);
      return null;
    }

    return {
      source: parsed.source ?? "public_paywall",
      createdAt: parsed.createdAt,
      handle: parsed.handle,
      previewSessionId: parsed.previewSessionId,
      checkoutSessionId: parsed.checkoutSessionId,
      plan: parsed.plan,
      currency: parsed.currency,
      value: typeof parsed.value === "number" ? parsed.value : undefined,
    };
  } catch {
    return null;
  }
}

export function clearPendingPurchaseContext() {
  try {
    sessionStorage.removeItem(PENDING_PURCHASE_KEY);
  } catch {
    // Session storage can be unavailable in private contexts.
  }
}

export function getPurchaseTrackingContext(
  search: string,
  pendingPurchase: PendingPurchaseContext | null = getPendingPurchaseContext(),
): {
  transactionId: string;
  plan: WebPlan;
  currency: WebBillingCurrency;
  value: number;
} | null {
  const searchParams = new URLSearchParams(search);
  const transactionId =
    searchParams.get("session_id") ?? pendingPurchase?.checkoutSessionId ?? "";
  const routePlan = searchParams.get("plan");
  const routeCurrency = searchParams.get("currency");
  const plan: WebPlan | undefined =
    routePlan === "weekly" || routePlan === "monthly"
      ? routePlan
      : pendingPurchase?.plan;
  const currency: WebBillingCurrency | undefined =
    routeCurrency === "brl" || routeCurrency === "usd"
      ? routeCurrency
      : pendingPurchase?.currency;

  if (!transactionId.startsWith("cs_") || !plan || !currency) return null;

  return {
    transactionId,
    plan,
    currency,
    value: WEB_PLAN_PRICES[currency][plan],
  };
}
